import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  Permission,
  RoleKey,
  type AuthenticatedUser,
  type LoginResponse,
} from '@marketing-os/shared';

interface AuthState {
  user: AuthenticatedUser | null;
  accessToken: string | null;
  refreshToken: string | null;
  setSession: (payload: LoginResponse) => void;
  setTokens: (accessToken: string, refreshToken: string) => void;
  setUser: (user: AuthenticatedUser) => void;
  clear: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      setSession: ({ user, accessToken, refreshToken }) => set({ user, accessToken, refreshToken }),
      setTokens: (accessToken, refreshToken) => set({ accessToken, refreshToken }),
      setUser: (user) => set({ user }),
      clear: () => set({ user: null, accessToken: null, refreshToken: null }),
    }),
    { name: 'marketing-os.auth' },
  ),
);

/* ------------------------------------------------------------------ *
 * Acesso imperativo (usado pelo interceptor do axios e pelo socket)
 * ------------------------------------------------------------------ */

export function getAccessToken(): string | null {
  return useAuthStore.getState().accessToken;
}

export function getRefreshToken(): string | null {
  return useAuthStore.getState().refreshToken;
}

export function hasPermission(permission: Permission | Permission[]): boolean {
  const user = useAuthStore.getState().user;
  if (!user) return false;
  const list = Array.isArray(permission) ? permission : [permission];
  return list.some((p) => user.permissions.includes(p));
}

export function isClientPortalUser(user: AuthenticatedUser | null): boolean {
  if (!user) return false;
  const portalRoles: RoleKey[] = [RoleKey.CLIENT_VIEWER, RoleKey.CLIENT_APPROVER];
  return user.roles.length > 0 && user.roles.every((role) => portalRoles.includes(role));
}

/** Hook com as capacidades derivadas do usuário logado. */
export function useAuth() {
  const user = useAuthStore((s) => s.user);
  const accessToken = useAuthStore((s) => s.accessToken);
  const clear = useAuthStore((s) => s.clear);

  const can = (permission: Permission | Permission[]): boolean => {
    if (!user) return false;
    const list = Array.isArray(permission) ? permission : [permission];
    return list.some((p) => user.permissions.includes(p));
  };

  return {
    user,
    isAuthenticated: Boolean(accessToken && user),
    isPortalUser: isClientPortalUser(user),
    can,
    canAll: (permissions: Permission[]) =>
      Boolean(user) && permissions.every((p) => user!.permissions.includes(p)),
    canDecideApprovals: can([Permission.APPROVAL_DECIDE, Permission.APPROVAL_DECIDE_CLIENT]),
    canManageBudget: can(Permission.BUDGET_MANAGE),
    /** null = acesso a todos os clientes da organização. */
    scopedClientIds: user?.clientIds ?? null,
    logout: clear,
  };
}
